import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    Divider,
    FormControl,
    FormControlLabel,
    InputBase,
    Link,
    Typography,
} from '@mui/material';
import { validateEmailAddress } from '@omsmastersystem/pavo-intl';
import { fetchUser } from 'actions';
import PavoDialog from 'components/Dialog/PavoDialog';
import PavoDetailTextField from 'components/TextField/PavoDetailTextField';
import {
    upgradePavoLogin,
    completeLoginUpgrade,
    verifyMFA,
    completeNewPavoUserSetup,
} from 'network/user';
import React from 'react';
import { MinimumPasswordLength } from 'util/utils';
import { useDispatch } from 'react-redux';
import { CheckCircle } from '@mui/icons-material';

function PavoLoginUpgradeDialog({ open, onClose }) {
    const [email, setEmail] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [mfaRequired, setMfaRequired] = React.useState(false);
    const [mfaCode, setMfaCode] = React.useState('');
    const [errorString, setErrorString] = React.useState(null);
    const [statusString, setStatusString] = React.useState('default');
    const [upgradeComplete, setUpgradeComplete] = React.useState(false);
    const [needAccountSetup, setNeedAccountSetup] = React.useState(false);
    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [agreeTerms, setAgreeTerms] = React.useState(false);

    const dispatch = useDispatch();

    const handleNewUserSetup = async () => {
        setLoading(true);
        setErrorString(null);
        setStatusString('default');
        try {
            await completeNewPavoUserSetup({
                first_name: firstName,
                last_name: lastName,
                password,
            });
            setNeedAccountSetup(false);
            setUpgradeComplete(true);
            dispatch(fetchUser());
        } catch (error) {
            setErrorString(error.message);
            setStatusString('error');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyEmail = async (resendCode = false) => {
        setLoading(true);
        setError(null);
        try {
            const res = await upgradePavoLogin({ email });
            if (res.status === 'error') {
                setError(res.message);
            } else {
                setMfaRequired(res.mfa_required);
                if (res.mfa_required) {
                    // Handle MFA required case
                    // You can show a dialog or redirect to MFA page
                    setMfaRequired(true);
                }
                if (resendCode) {
                    setErrorString('Verification code resent successfully.');
                    setStatusString('green');
                }
            }
        } catch (err) {
            setError('Failed to verify email. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleMfaSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setErrorString(null);
        setStatusString('default');
        try {
            await verifyMFA(mfaCode);
        } catch (error) {
            setErrorString(error.message);
            setStatusString('error');
            setLoading(false);
            return;
        }
        const res = await completeLoginUpgrade();
        if (res.status === 'error') {
            setErrorString(res.message);
            setStatusString('error');
        } else if (res.user_info.initial_setup) {
            // present initial setup dialog to set name and password
            setNeedAccountSetup(true);
        } else {
            setUpgradeComplete(true);
            dispatch(fetchUser());
        }
        setLoading(false);
    };

    const renderContent = () => {
        if (needAccountSetup) {
            let disabled = false;
            if (password !== confirmPassword) {
                disabled = true;
            }
            if (password.length < MinimumPasswordLength) {
                disabled = true;
            }
            if (!agreeTerms) {
                disabled = true;
            }
            if (!firstName) {
                disabled = true;
            }
            if (!password || !confirmPassword) {
                disabled = true;
            }

            return (
                <>
                    <DialogContent>
                        <Typography gutterBottom>{`Email: ${email}`}</Typography>
                        <Typography>{'Name:'}</Typography>
                        <PavoDetailTextField
                            label={'First Name'}
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                        />
                        <PavoDetailTextField
                            label={'Last Name'}
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                        />
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            {'Set Password'}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {`Please set a password for your Pavo account. Passwords must be at least ${MinimumPasswordLength} characters.`}
                        </Typography>
                        <PavoDetailTextField
                            label={'Password'}
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <PavoDetailTextField
                            label={'Confirm Password'}
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                        <Divider sx={{ my: 2 }} />
                        <FormControl component="fieldset">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={agreeTerms}
                                        onChange={(e) => setAgreeTerms(e.target.checked)}
                                    />
                                }
                                label={
                                    <>
                                        {`I agree to the Master System `}
                                        <a
                                            target="_blank"
                                            rel="noreferrer"
                                            href={'https://www.mastersystem.com/terms-conditions'}
                                        >
                                            {'Terms & Conditions'}
                                        </a>
                                        {` and `}{' '}
                                        <a
                                            target="_blank"
                                            rel="noreferrer"
                                            href={'https://www.mastersystem.com/privacy-policy'}
                                        >
                                            {'Privacy Policy'}
                                        </a>
                                        {'.'}
                                    </>
                                }
                            />
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <LoadingButton
                            disabled={disabled}
                            loading={loading}
                            variant="contained"
                            onClick={handleNewUserSetup}
                        >
                            {'Complete Upgrade'}
                        </LoadingButton>
                    </DialogActions>
                </>
            );
        }
        if (upgradeComplete) {
            return (
                <>
                    <DialogContent>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                            <CheckCircle fontSize={'large'} sx={{ color: 'green' }} />
                        </Box>

                        <Typography textAlign="center" variant="h6">
                            {'Pavo Login Upgrade Complete'}
                        </Typography>

                        <Typography variant="body1" gutterBottom>
                            {
                                'Your account has been upgraded to use a Pavo login. You can now log in with your email address and password and your OMS login will no longer be used.'
                            }
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setUpgradeComplete(false);
                                setErrorString(null);
                                setStatusString('default');
                                onClose();
                            }}
                        >
                            {'Close'}
                        </Button>
                    </DialogActions>
                </>
            );
        }
        if (mfaRequired) {
            return (
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        {'Please enter the 6-digit code sent to your email address.'}
                    </Typography>
                    <Typography color={statusString} align="center" gutterBottom>
                        {errorString}
                    </Typography>
                    <Box
                        component="form"
                        id="mfa-form"
                        sx={{ width: '450px' }}
                        onSubmit={handleMfaSubmit}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            {Array.from({ length: 6 }).map((_, index) => (
                                <InputBase
                                    key={index}
                                    inputProps={{
                                        maxLength: 1,
                                        inputMode: 'numeric',
                                        style: {
                                            textAlign: 'center',
                                            fontSize: '1.5rem',
                                            width: '3rem',
                                            height: '3rem',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                        },
                                    }}
                                    value={mfaCode[index] || ''}
                                    onChange={(e) => {
                                        const newCode = mfaCode.split('');
                                        newCode[index] = e.target.value.replace(/[^0-9]/g, '');
                                        setMfaCode(newCode.join('').slice(0, 6));
                                        const nextInput = document.getElementById(
                                            `mfa-input-${index + 1}`,
                                        );
                                        if (nextInput && e.target.value) nextInput.focus();
                                        if (newCode.join('').length === 6) {
                                            setTimeout(() => {
                                                const form = document.getElementById('mfa-form');
                                                if (form) form.requestSubmit();
                                            }, 100);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Backspace' && !mfaCode[index] && index > 0) {
                                            const prevInput = document.getElementById(
                                                `mfa-input-${index - 1}`,
                                            );
                                            if (prevInput) prevInput.focus();
                                        }
                                    }}
                                    onPaste={(e) => {
                                        const pasted = e.clipboardData
                                            .getData('text')
                                            .replace(/\D/g, '');
                                        if (pasted.length === 6) {
                                            setMfaCode(pasted.slice(0, 6));
                                            setTimeout(() => {
                                                const form = document.getElementById('mfa-form');
                                                if (form) form.requestSubmit();
                                            }, 100);
                                            e.preventDefault();
                                        }
                                    }}
                                    id={`mfa-input-${index}`}
                                />
                            ))}
                        </Box>
                        <Box sx={{ pt: 1, pb: 1 }}>
                            <Link
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={() => handleVerifyEmail(true)}
                            >
                                {'Resend code'}
                            </Link>
                        </Box>
                        <LoadingButton
                            type="submit"
                            fullWidth
                            disabled={mfaCode.length !== 6}
                            loading={loading}
                            variant="contained"
                            color="primary"
                            size="large"
                            sx={{ mt: 1, mb: 1 }}
                        >
                            {'Verify Code'}
                        </LoadingButton>
                        <Button
                            variant="outlined"
                            size="large"
                            fullWidth
                            onClick={() => {
                                setMfaRequired(false);
                                setErrorString(null);
                                setStatusString('default');
                            }}
                        >
                            {'Back'}
                        </Button>
                    </Box>
                </DialogContent>
            );
        }

        return (
            <>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        {
                            'Unify your logins by using the same email address and password for all Pavo services.'
                        }
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                        {
                            'Once completed, you will not be able to log into Pavo Sales Rep with your OMS credentials.'
                        }
                    </Typography>
                    {error && (
                        <Typography variant="body2" color="error" gutterBottom>
                            {error}
                        </Typography>
                    )}
                    <PavoDetailTextField
                        label={'Email'}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <Button onClick={onClose}>{'Close'}</Button>
                        <LoadingButton
                            loading={loading}
                            variant="contained"
                            disabled={!validateEmailAddress(email)}
                            onClick={() => handleVerifyEmail()}
                        >
                            {'Verify Email'}
                        </LoadingButton>
                    </Box>
                </DialogActions>
            </>
        );
    };

    return (
        <PavoDialog title="Upgrade to Pavo Login" open={open}>
            {renderContent()}
        </PavoDialog>
    );
}

export default PavoLoginUpgradeDialog;
